/* eslint-disable react/no-array-index-key */
import React, { useEffect, useState, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Row, Col, UncontrolledTooltip } from 'reactstrap';
import { Controller, useForm } from 'react-hook-form';
import PropTypes, { shape } from 'prop-types';

import { getDocumentDetails } from 'redux/actions/documentActions';
import { goBackBasicData } from 'redux/actions/notificationActions';

import { ReactComponent as DocumentIcon } from 'assets/icon-document.svg';
import { ReactComponent as PdfIcon } from 'assets/icon-pdf.svg';
import { ReactComponent as WordIcon } from 'assets/icon-word.svg';
import { ReactComponent as ImgIcon } from 'assets/icon-img.svg';
import { ReactComponent as ExcelIcon } from 'assets/icon-excel.svg';
import { ReactComponent as InformationIcon } from 'assets/icon-information.svg';

import { LabelError, LabelRequiredInput } from 'shared/components/Labels';
import { Button } from 'shared/components/Buttons';
import { TextField } from 'shared/components/form/Inputs';
import MultipleSelectSearch from 'shared/components/MultipleSelectSearch';
import { colors } from 'shared/defaultTheme';
import { cleanSpaceString } from 'shared/utils';

import { FormWrapper, GroupInputWrapper } from './styles';

const fileType = {
  pdf: 'application/pdf',
  pdfFile: '.pdf',
  wordFile: '.docx',
  excelFile: '.xlsx',
  jpgFile: '.jpeg',
  pngFile: '.png',
  svgFile: '.svg',
  excel: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  word: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  text: 'text/plain',
  jpg: 'image/jpeg',
  png: 'image/png',
  svg: 'image/svg+xml',
};

const NotificationForm = ({ onCancel, onSubmit, clients, isDetailCommunication, clientId }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { isCustomer, mail, admins } = useSelector((state) => state.auth);
  const { newCommunication, currentClient } = useSelector((state) => state.clientState);
  const newName = useRef(null);
  const [loadingContact, setLoadingContact] = useState(false);
  const {
    control,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
  } = useForm({
    mode: 'onBlur',
    reValidateMode: 'onChange',
  });
  newName.current = watch('name', '');

  useEffect(() => {
    if (isDetailCommunication) {
      setValue('name', '');
    }
  }, [isDetailCommunication]);

  useEffect(() => {
    if (isDetailCommunication) {
      setValue('name', '');
      if (!isCustomer) {
        setValue('linkToContact', [mail]);
      } else {
        setValue('linkToContact', []);
      }
    }
  }, [isDetailCommunication]);

  useEffect(() => {
    setTimeout(() => {
      setValue('responsibles', [mail]);
      if (clientId) {
        setValue('clientsId', [clientId]);
      }
      setLoadingContact(true);
    }, 900);
    return () => {
      setLoadingContact(false);
      dispatch(getDocumentDetails(null));
    };
  }, [newCommunication, clientId]);

  useEffect(() => {
    if (currentClient) {
      setValue('clientsId', [currentClient.clientId]);
    }
  }, [currentClient]);

  const IconFile = ({ file }) => {
    if (file.type === fileType.word || file.type === fileType.wordFile) {
      return <WordIcon fill={colors.suvaGrey} className="control-panel__icon-type" width="20" />;
    }
    if (file.type === fileType.pdf || file.type === fileType.pdfFile) {
      return <PdfIcon fill={colors.suvaGrey} className="control-panel__icon-type" width="20" />;
    }
    if (
      file.type === fileType.jpg ||
      file.type === fileType.png ||
      file.type === fileType.svg ||
      file.type === fileType.jpgFile ||
      file.type === fileType.pngFile ||
      file.type === fileType.svgFile
    ) {
      return <ImgIcon fill={colors.suvaGrey} className="control-panel__icon-type" width="20" />;
    }
    if (file.type === fileType.excel || file.type === fileType.excelFile) {
      return <ExcelIcon fill={colors.suvaGrey} className="control-panel__icon-type" width="20" />;
    }
    return <DocumentIcon fill={colors.suvaGrey} className="control-panel__icon-type" width="20" />;
  };

  IconFile.propTypes = {
    file: PropTypes.shape({
      type: PropTypes.string,
    }).isRequired,
  };

  return (
    <>
      <FormWrapper className="form" noValidate onSubmit={handleSubmit(onSubmit)}>
        <Row>
          <Col md="12" className="mb-4">
            <Row className="form-container">
              <Col md="12">
                <Row>
                  <Col>
                    <GroupInputWrapper className="group-input-container">
                      <div className="d-flex">
                        <LabelRequiredInput
                          label={t('notification_name_communication')}
                          color={colors.black}
                          className="label-input-notify"
                        />
                        <InformationIcon
                          id="notify-name-form-help"
                          height={17}
                          width={17}
                          className="ml-1 information-icon"
                          fill={colors.error}
                        />
                        <UncontrolledTooltip
                          placement="top"
                          target="notify-name-form-help"
                          autohide={false}
                        >
                          {t('notification_name_info')}
                        </UncontrolledTooltip>
                      </div>

                      <Controller
                        name="name"
                        control={control}
                        defaultValue=""
                        rules={{
                          required: { value: true, message: t('general_required_field') },
                        }}
                        render={({ field }) => (
                          <TextField
                            value={field.value}
                            onChange={(e) => {
                              const newEvent = cleanSpaceString(e);
                              field.onChange(newEvent);
                            }}
                            onBlur={field.onBlur}
                            error={errors.name !== undefined}
                            borderRadius={2}
                            inputClassName="w-100"
                            buttonClassName=""
                            withButtonClear={false}
                          />
                        )}
                      />
                      {errors.name && <LabelError label={errors.name.message} />}
                    </GroupInputWrapper>
                    {isCustomer && (
                      <>
                        <GroupInputWrapper className="group-input-container">
                          <LabelRequiredInput
                            label={t('home_client_section_label')}
                            color={colors.black}
                            className="label-input-notify"
                          />
                          <Controller
                            name="clientsId"
                            control={control}
                            defaultValue={[]}
                            rules={{
                              required: { value: true, message: t('general_required_field') },
                            }}
                            render={({ field }) => (
                              <>
                                {loadingContact && (
                                  <MultipleSelectSearch
                                    dataSource={clients}
                                    fields={{ text: 'clientName', value: 'clientId' }}
                                    defaultValue={field.value.length > 0 ? field.value : undefined}
                                    onBlur={field.onBlur}
                                    onChange={field.onChange}
                                    error={errors.clientsId !== undefined}
                                    titleNotFound={t('general_search')}
                                  />
                                )}
                              </>
                            )}
                          />
                          {errors.clientsId && <LabelError label={errors.clientsId.message} />}
                        </GroupInputWrapper>
                      </>
                    )}
                    <GroupInputWrapper className="group-input-container">
                      <LabelRequiredInput
                        label={t('notification_person_responsible_field')}
                        color={colors.black}
                        className="label-input-notify"
                      />
                      <Controller
                        name="responsibles"
                        control={control}
                        defaultValue={[]}
                        rules={{
                          required: { value: true, message: t('general_required_field') },
                        }}
                        render={({ field }) => (
                          <>
                            {loadingContact && admins && admins.length > 0 && (
                              <MultipleSelectSearch
                                dataSource={admins.filter((x) => x.active)}
                                fields={{ text: 'nameComplete', value: 'mail' }}
                                defaultValue={field.value.length > 0 ? field.value : undefined}
                                onChange={field.onChange}
                                onBlur={field.onBlur}
                                error={errors.responsibles !== undefined}
                              />
                            )}
                          </>
                        )}
                      />
                      {errors.responsibles && <LabelError label={errors.responsibles.message} />}
                    </GroupInputWrapper>
                  </Col>
                </Row>
              </Col>
            </Row>
          </Col>
        </Row>
        <div className="ml-auto pt-2">
          <Button
            className="home__btn text-uppercase"
            type="submit"
            label={t('general_button_continue')}
            color="primary"
          />
          <Button
            className="home__btn text-uppercase mr-4"
            type="button"
            label={t('general_button_cancel')}
            color="secondary"
            onClick={() => {
              if (
                newCommunication &&
                newCommunication !== 'fromClient' &&
                newCommunication !== 'fromDocument'
              ) {
                onCancel();
                dispatch(goBackBasicData(true));
              } else {
                onCancel();
              }
            }}
          />
        </div>
      </FormWrapper>
    </>
  );
};

NotificationForm.defaultProps = {
  file: undefined,
  isDetailCommunication: false,
  clientId: '',
};

NotificationForm.propTypes = {
  onCancel: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  clients: PropTypes.arrayOf(
    shape({
      id: PropTypes.string,
      clientName: PropTypes.string,
    })
  ).isRequired,
  file: PropTypes.shape({
    name: PropTypes.string,
    size: PropTypes.number,
  }),
  isDetailCommunication: PropTypes.bool,
  clientId: PropTypes.string,
};

export default NotificationForm;
